import React, { useState, useEffect } from 'react';
import {
  UserCircleIcon,
  BuildingOfficeIcon,
  DocumentTextIcon,
  CurrencyDollarIcon,
  ScaleIcon,
  ExclamationCircleIcon,
  SparklesIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useMutation } from '@tanstack/react-query';
import { Lawsuit, User, SupportedCountry } from '@tvia-ai/shared';
import GoogleAddressInput from '../ui/GoogleAddressInput';
import type { FormattedAddress } from '../../hooks/useGoogleMapsApi';
import SummaryCard from './SummaryCard';
import EvidenceCard from './EvidenceCard';
import ProgressiveDisclosure from './ProgressiveDisclosure';

interface VerificationStepProps {
  data: Lawsuit;
  user: User;
  onUpdate: (updates: Partial<Lawsuit>) => void;
  onNext: () => void;
  onBack: () => void;
  isEditMode?: boolean;
}

const VerificationStep: React.FC<VerificationStepProps> = ({
  data,
  user,
  onUpdate,
  onNext,
  onBack,
  isEditMode = false
}) => {
  const { t } = useTranslation();

  // State management
  const [editState, setEditState] = useState<{ [key: string]: boolean }>({});
  const [localEdits, setLocalEdits] = useState<{ [key: string]: any }>({});
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [executiveSummary, setExecutiveSummary] = useState<string>('');
  const [loadingSummary, setLoadingSummary] = useState(false);

  // Google search related state
  const [isSearching, setIsSearching] = useState(false);
  const [searchSuccessMessage, setSearchSuccessMessage] = useState<string>('');

  // Required fields for validation
  const requiredFields = [
    { path: 'accusedParty.name', label: t('filing.verification.errors.accusedName', 'Name'), section: t('filing.verification.sections.accusedParty', 'Accused Party') },
    { path: 'accusedParty.id', label: t('filing.verification.errors.accusedId', 'ID'), section: t('filing.verification.sections.accusedParty', 'Accused Party') },
    { path: 'accusedParty.address', label: t('filing.verification.errors.accusedAddress', 'Address'), section: t('filing.verification.sections.accusedParty', 'Accused Party') },
    { path: 'accusedParty.contact', label: t('filing.verification.errors.accusedContact', 'Contact'), section: t('filing.verification.sections.accusedParty', 'Accused Party') },
    { path: 'userInfo.firstName', label: t('filing.verification.errors.userFirstName', 'First Name'), section: t('filing.verification.sections.yourInformation', 'Your Information') },
    { path: 'userInfo.lastName', label: t('filing.verification.errors.userLastName', 'Last Name'), section: t('filing.verification.sections.yourInformation', 'Your Information') },
    { path: 'userInfo.email', label: t('filing.verification.errors.userEmail', 'Email'), section: t('filing.verification.sections.yourInformation', 'Your Information') },
    { path: 'userInfo.phone', label: t('filing.verification.errors.userPhone', 'Phone Number'), section: t('filing.verification.sections.yourInformation', 'Your Information') },
    { path: 'userInfo.address', label: t('filing.verification.errors.userAddress', 'Address'), section: t('filing.verification.sections.yourInformation', 'Your Information') },
    { path: 'aiAnalysis.estimatedTimeline', label: t('filing.verification.errors.estimatedTimeline', 'Estimated Timeline'), section: t('filing.verification.sections.caseSummary', 'Case Summary') },
    { path: 'aiAnalysis.estimatedClaimAmount.amount', label: t('filing.verification.errors.estimatedClaimAmount', 'Estimated Claim Amount'), section: t('filing.verification.sections.estimatedClaimAmount', 'Estimated Claim Amount') },
  ];

  // Auto-populate userInfo from user profile if missing
  useEffect(() => {
    if (!data.userInfo || Object.keys(data.userInfo).length === 0) {
      // If userInfo is completely missing, populate from user profile
      const populatedUserInfo = {
        firstName: user?.firstName || '',
        lastName: user?.lastName || '',
        email: user?.email || '',
        phone: user?.phone || '',
        address: user?.address || '',
        country: (user?.country as SupportedCountry) || 'US',
        dateOfBirth: '',
        occupation: '',
      };
      onUpdate({ userInfo: populatedUserInfo });
    } else {
      // If userInfo exists but some fields are missing, fill them from user profile
      const updates: any = {};
      let hasUpdates = false;

      if (!data.userInfo.firstName && user?.firstName) {
        updates.firstName = user.firstName;
        hasUpdates = true;
      }
      if (!data.userInfo.lastName && user?.lastName) {
        updates.lastName = user.lastName;
        hasUpdates = true;
      }
      if (!data.userInfo.email && user?.email) {
        updates.email = user.email;
        hasUpdates = true;
      }
      if (!data.userInfo.phone && user?.phone) {
        updates.phone = user.phone;
        hasUpdates = true;
      }
      if (!data.userInfo.address && user?.address) {
        updates.address = user.address;
        hasUpdates = true;
      }
      if (!data.userInfo.country && user?.country) {
        updates.country = user.country as SupportedCountry;
        hasUpdates = true;
      }

      if (hasUpdates) {
        onUpdate({
          userInfo: {
            ...data.userInfo,
            ...updates
          }
        });
      }
    }
  }, []);

  // Load executive summary on mount
  useEffect(() => {
    const fetchExecutiveSummary = async () => {
      // Check if summary already exists in aiAnalysis
      if (data.aiAnalysis?.executiveSummary) {
        setExecutiveSummary(data.aiAnalysis.executiveSummary);
        return;
      }

      // Generate new summary
      setLoadingSummary(true);
      try {
        const response = await axios.post('/api/ai/generate-executive-summary', {
          lawsuitId: data.id
        });
        if (response.data.success && response.data.data.summary) {
          setExecutiveSummary(response.data.data.summary);
        }
      } catch (error) {
        console.error('Failed to generate executive summary:', error);
      } finally {
        setLoadingSummary(false);
      }
    };

    fetchExecutiveSummary();
  }, [data.id, data.aiAnalysis?.executiveSummary]);

  // Edit handlers
  const startEdit = (section: string) => {
    setEditState(s => ({ ...s, [section]: true }));
    let currentData;
    if (section === 'accusedParty') {
      currentData = data.aiAnalysis?.accusedParty || {};
    } else if (section === 'userInfo') {
      currentData = {
        firstName: data.userInfo?.firstName || user?.firstName || '',
        lastName: data.userInfo?.lastName || user?.lastName || '',
        email: data.userInfo?.email || user?.email || '',
        phone: data.userInfo?.phone || user?.phone || '',
        address: data.userInfo?.address || user?.address || '',
        country: data.userInfo?.country || (user?.country as SupportedCountry) || 'US',
        dateOfBirth: data.userInfo?.dateOfBirth || '',
        occupation: data.userInfo?.occupation || '',
      };
    } else if (section === 'summary') {
      currentData = {
        text: data.aiAnalysis?.summary || '',
        estimatedTimeline: data.aiAnalysis?.estimatedTimeline ? new Date(data.aiAnalysis.estimatedTimeline) : null
      };
    } else if (section === 'estimatedClaimAmount') {
      currentData = data.aiAnalysis?.estimatedClaimAmount || {};
    } else {
      currentData = {};
    }
    setLocalEdits(prev => ({ ...prev, [section]: currentData }));
  };

  const stopEdit = (section: string) => {
    setEditState(s => ({ ...s, [section]: false }));
    setLocalEdits(prev => {
      const newEdits = { ...prev };
      delete newEdits[section];
      return newEdits;
    });
  };

  const handleChange = (section: string, field: string, value: any) => {
    setLocalEdits(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const getDefaultAIAnalysis = (existing?: any) => ({
    caseTitle: existing?.caseTitle || '',
    summary: existing?.summary || '',
    detailedSummary: existing?.detailedSummary || '',
    executiveSummary: existing?.executiveSummary || '',
    category: existing?.category || 'other',
    totalCost: existing?.totalCost || 0,
    currency: existing?.currency || 'USD',
    estimatedClaimAmount: existing?.estimatedClaimAmount || {
      amount: 0,
      currency: 'USD',
      justification: '',
      breakdown: [],
    },
    legalBasis: existing?.legalBasis || [],
    strengthScore: existing?.strengthScore || 0,
    confidence: existing?.confidence || 0,
    countrySpecificFactors: existing?.countrySpecificFactors || [],
    similarCases: existing?.similarCases || [],
    followUpQuestions: existing?.followUpQuestions || [],
    estimatedTimeline: existing?.estimatedTimeline || new Date(),
    jurisdictionNotes: existing?.jurisdictionNotes || '',
    classActionViability: existing?.classActionViability || { recommended: false, reason: '', potentialParticipants: '' },
    analysisDate: existing?.analysisDate || new Date(),
    aiModel: existing?.aiModel || 'gemini-2.0-flash',
    accusedParty: existing?.accusedParty || { name: '', details: '' },
    generatedDocuments: existing?.generatedDocuments || {},
  });

  const saveEdit = (section: string) => {
    const sectionData = localEdits[section];
    if (!sectionData) return stopEdit(section);

    if (section === 'userInfo') {
      onUpdate({ userInfo: sectionData });
    } else if (section === 'summary') {
      onUpdate({
        aiAnalysis: {
          ...getDefaultAIAnalysis(data.aiAnalysis),
          summary: sectionData.text || '',
          estimatedTimeline: sectionData.estimatedTimeline || new Date(),
        },
      });
    } else if (section === 'accusedParty') {
      onUpdate({
        aiAnalysis: {
          ...getDefaultAIAnalysis(data.aiAnalysis),
          accusedParty: sectionData,
        },
      });
    } else if (section === 'estimatedClaimAmount') {
      const totalAmount = (sectionData.breakdown || []).reduce((sum: number, item: any) => sum + (parseFloat(item.amount) || 0), 0);
      onUpdate({
        aiAnalysis: {
          ...getDefaultAIAnalysis(data.aiAnalysis),
          estimatedClaimAmount: {
            ...sectionData,
            amount: totalAmount,
          },
        },
      });
    }
    stopEdit(section);
  };

  // Handle Google search for accused party
  const handleGoogleSearch = async () => {
    const currentName = localEdits.accusedParty?.name || data.aiAnalysis?.accusedParty?.name || '';

    if (!currentName?.trim()) {
      setErrors({ ...errors, googleSearch: t('filing.verification.accusedParty.enterNameFirst', 'Please enter the accused party name first.') });
      return;
    }

    const currentAccusedParty = {
      ...data.aiAnalysis?.accusedParty,
      ...localEdits.accusedParty
    };

    const jurisdictionCountry = data.country;
    const additionalContext = currentAccusedParty.details || '';

    try {
      setIsSearching(true);
      setErrors({ ...errors, googleSearch: '' });
      setSearchSuccessMessage('');

      const result = await searchAccusedPartyMutation.mutateAsync({
        name: currentName.trim(),
        country: jurisdictionCountry,
        additionalContext: additionalContext.trim()
      });

      if (result.success && result.data) {
        const formatBusinessDetails = (data: any) => {
          const parts = [];
          if (data.businessType) parts.push(`Type: ${data.businessType}`);
          if (data.industry) parts.push(`Industry: ${data.industry}`);
          if (data.foundedYear) parts.push(`Founded: ${data.foundedYear}`);
          if (data.employees) parts.push(`Employees: ${data.employees}`);
          if (data.revenue) parts.push(`Revenue: ${data.revenue}`);
          if (data.status) parts.push(`Status: ${data.status}`);
          if (data.details) parts.push(data.details);
          return parts.join(' | ');
        };

        const updatedAccusedParty = {
          ...currentAccusedParty,
          name: result.data.fullName || currentName || '',
          id: result.data.id || currentAccusedParty.id || '',
          address: result.data.address || currentAccusedParty.address || '',
          contact: result.data.contact || currentAccusedParty.contact || '',
          details: formatBusinessDetails(result.data) || currentAccusedParty.details || '',
          isBusiness: result.data.isBusiness !== undefined ? result.data.isBusiness : currentAccusedParty.isBusiness
        };

        setLocalEdits(prev => ({
          ...prev,
          accusedParty: updatedAccusedParty
        }));

        setSearchSuccessMessage(t('filing.verification.accusedParty.searchSuccess', 'Information found and populated!'));
        setTimeout(() => setSearchSuccessMessage(''), 5000);
      } else {
        setErrors({ ...errors, googleSearch: t('filing.verification.accusedParty.searchNoResults', 'No information found. Please fill in manually.') });
      }
    } catch (error: any) {
      setErrors({ ...errors, googleSearch: error?.response?.data?.error || t('filing.verification.accusedParty.searchError', 'Search failed. Please try again or fill in manually.') });
    } finally {
      setIsSearching(false);
    }
  };

  // Validation function
  const validate = () => {
    const newErrors: { [key: string]: string } = {};
    requiredFields.forEach(field => {
      if (field.path === 'aiAnalysis.estimatedTimeline') {
        let value = data.aiAnalysis?.estimatedTimeline;
        if (editState['summary'] && localEdits.summary?.estimatedTimeline) {
          value = localEdits.summary.estimatedTimeline;
        }
        if (!value) {
          newErrors[field.path] = `${field.section}, ${field.label} is required.`;
          return;
        }
        const isValidDate = value instanceof Date && !isNaN(value.getTime());
        const isValidDateString = typeof value === 'string' && !isNaN(Date.parse(value));
        if (!isValidDate && !isValidDateString) {
          newErrors[field.path] = `${field.section}, ${field.label} is required.`;
        }
        return;
      }
      let value;
      if (field.path.startsWith('accusedParty.')) {
        value = (data.aiAnalysis?.accusedParty as any)?.[field.path.split('.')[1]];
      } else if (field.path.startsWith('userInfo.')) {
        value = (data.userInfo as any)?.[field.path.split('.')[1]];
      } else if (field.path.startsWith('aiAnalysis.estimatedClaimAmount.')) {
        value = (data.aiAnalysis?.estimatedClaimAmount as any)?.[field.path.split('.')[2]];
      } else {
        value = (data.aiAnalysis as any)?.[field.path.split('.')[1]];
      }
      if (!value || (typeof value === 'string' && !value.trim()) || (typeof value === 'object' && 'length' in value && !value.length)) {
        newErrors[field.path] = `${field.section}, ${field.label} is required.`;
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // API mutation for updating lawsuit
  const updateLawsuitMutation = useMutation({
    mutationFn: async (payload: any) => {
      const response = await axios.put(`/api/lawsuits/${data.id}`, payload);
      return response.data;
    },
  });

  // Google search mutation for accused party information
  const searchAccusedPartyMutation = useMutation({
    mutationFn: async ({ name, country, additionalContext }: { name: string; country: SupportedCountry; additionalContext?: string }) => {
      const response = await axios.post('/api/ai/search-accused-party', {
        name,
        country,
        additionalContext
      });
      return response.data;
    },
  });

  // Handle Next with validation and API update
  const handleNext = async () => {
    setIsSubmitting(true);
    if (editState['summary']) {
      saveEdit('summary');
    }
    if (!validate()) {
      setIsSubmitting(false);
      return;
    }
    try {
      await updateLawsuitMutation.mutateAsync(data);
      setIsSubmitting(false);
      onNext();
    } catch (error: any) {
      setIsSubmitting(false);
      setErrors({ api: error?.response?.data?.error || 'Failed to update lawsuit.' });
    }
  };

  // Data extraction
  const title = data.aiAnalysis?.caseTitle || data.title || '';
  const strength = data.aiAnalysis?.strengthScore ?? '-';
  const probability = Math.round((data.aiAnalysis?.confidence ?? 0) * 100);
  const accused = data.aiAnalysis?.accusedParty || { name: '', id: '', address: '', contact: '', details: '' };
  const summary = data.aiAnalysis?.summary || '';
  const userInfo = {
    firstName: data.userInfo?.firstName || user?.firstName || '',
    lastName: data.userInfo?.lastName || user?.lastName || '',
    email: data.userInfo?.email || user?.email || '',
    phone: data.userInfo?.phone || user?.phone || '',
    address: data.userInfo?.address || user?.address || '',
    country: (data.userInfo?.country || user?.country as SupportedCountry) || 'US',
    dateOfBirth: data.userInfo?.dateOfBirth || '',
    occupation: data.userInfo?.occupation || '',
  };
  const estimatedClaimAmount = data.aiAnalysis?.estimatedClaimAmount || {
    amount: 0,
    currency: 'USD',
    justification: '',
    breakdown: [],
  };

  return (
    <div className="max-w-5xl mx-auto pb-32">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2" style={{ fontSize: '1.5rem' }}>
          {t('filing.verification.title', 'Verify Your Case Details')}
        </h2>
        <h1 className="text-3xl font-extrabold text-blue-800 mb-4" style={{ fontSize: '1.875rem' }}>
          {title}
        </h1>
      </div>

      {/* Executive Summary Card */}
      {(executiveSummary || loadingSummary) && (
        <div className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl shadow-md border border-blue-200 p-6">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0">
              <SparklesIcon className="w-6 h-6 text-blue-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-blue-900 mb-2 flex items-center gap-2">
                {t('filing.verification.executiveSummary', 'Claim Summary')}
                <span className="text-xs bg-blue-200 text-blue-800 px-2 py-0.5 rounded-full">
                  AI-Generated
                </span>
              </h3>
              {loadingSummary ? (
                <div className="flex items-center gap-2 text-blue-700">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"></div>
                  <span className="text-sm">{t('common.loading', 'Loading...')}</span>
                </div>
              ) : (
                <p className="text-gray-800 leading-relaxed" style={{ fontSize: '1rem', lineHeight: '1.625' }}>
                  {executiveSummary}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Case Strength & Success Probability */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-3">
            <ScaleIcon className="w-8 h-8 text-blue-600" />
            <div>
              <p className="text-sm text-gray-600" style={{ fontSize: '0.875rem' }}>
                {t('filing.verification.caseStrength', 'Case Strength')}
              </p>
              <p className="text-3xl font-bold text-blue-700">{strength}/100</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-3">
            <ScaleIcon className="w-8 h-8 text-green-600" />
            <div>
              <p className="text-sm text-gray-600" style={{ fontSize: '0.875rem' }}>
                {t('filing.verification.successProbability', 'Success Probability')}
              </p>
              <p className="text-3xl font-bold text-green-700">{probability}%</p>
            </div>
          </div>
        </div>
      </div>

      {/* Bento Grid Layout */}
      <div className="space-y-6">
        {/* Accused Party Card */}
        <SummaryCard
          title={t('filing.verification.sections.accusedParty', 'Accused Party')}
          icon={<BuildingOfficeIcon className="w-6 h-6" />}
          editable
          onEdit={() => startEdit('accusedParty')}
          required
        >
          {editState['accusedParty'] ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2" style={{ fontSize: '0.875rem' }}>
                  {t('filing.verification.accusedParty.name', 'Name')} <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-3">
                  <input
                    className="flex-1 border border-gray-300 rounded-lg px-4 py-3 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 min-h-[44px]"
                    style={{ fontSize: '1rem' }}
                    value={localEdits.accusedParty?.name ?? accused.name ?? ''}
                    onChange={e => handleChange('accusedParty', 'name', e.target.value)}
                    placeholder={t('filing.verification.accusedParty.namePlaceholder', 'Name of the accused party')}
                  />
                  <button
                    type="button"
                    onClick={handleGoogleSearch}
                    disabled={!localEdits.accusedParty?.name?.trim() || isSearching}
                    className="px-4 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg hover:from-green-600 hover:to-emerald-700 transition-all shadow-sm hover:shadow-md disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed flex items-center gap-2 min-h-[44px] font-medium"
                    title={t('filing.verification.accusedParty.searchTooltip', 'Search for accused party information using AI')}
                    style={{ fontSize: '1rem' }}
                  >
                    <MagnifyingGlassIcon className="w-5 h-5" />
                    {isSearching ? t('filing.verification.accusedParty.searching', 'Searching...') : t('filing.verification.accusedParty.search', 'Search')}
                  </button>
                </div>
                {errors.googleSearch && (
                  <p className="mt-2 text-sm text-red-600">{errors.googleSearch}</p>
                )}
                {searchSuccessMessage && (
                  <p className="mt-2 text-sm text-green-600 flex items-center gap-1">
                    <span className="inline-block w-1.5 h-1.5 bg-green-600 rounded-full"></span>
                    {searchSuccessMessage}
                  </p>
                )}
                {isSearching && (
                  <div className="mt-3 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="flex-shrink-0">
                        <div className="animate-spin rounded-full h-5 w-5 border-2 border-blue-600 border-t-transparent"></div>
                      </div>
                      <div className="flex-1">
                        <p className="text-blue-800 font-medium text-sm">
                          {t('filing.verification.accusedParty.searchInProgress', 'Search in progress...')}
                        </p>
                        <p className="text-blue-600 text-xs mt-1">
                          {t('filing.verification.accusedParty.searchingForInfo', 'Searching for accused party information')}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2" style={{ fontSize: '0.875rem' }}>
                  {t('filing.verification.accusedParty.type', 'Type')}
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center min-h-[44px]">
                    <input
                      type="radio"
                      checked={localEdits.accusedParty?.isBusiness !== false}
                      onChange={() => handleChange('accusedParty', 'isBusiness', true)}
                      className="h-5 w-5 text-blue-600"
                    />
                    <span className="ml-2 text-gray-700" style={{ fontSize: '1rem' }}>
                      {t('filing.verification.accusedParty.businessOrganization', 'Business/Organization')}
                    </span>
                  </label>
                  <label className="flex items-center min-h-[44px]">
                    <input
                      type="radio"
                      checked={localEdits.accusedParty?.isBusiness === false}
                      onChange={() => handleChange('accusedParty', 'isBusiness', false)}
                      className="h-5 w-5 text-blue-600"
                    />
                    <span className="ml-2 text-gray-700" style={{ fontSize: '1rem' }}>
                      {t('filing.verification.accusedParty.individualPerson', 'Individual Person')}
                    </span>
                  </label>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2" style={{ fontSize: '0.875rem' }}>
                  {t('filing.verification.accusedParty.id', 'ID')} <span className="text-red-500">*</span>
                </label>
                <input
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 min-h-[44px]"
                  style={{ fontSize: '1rem' }}
                  value={localEdits.accusedParty?.id ?? accused.id ?? ''}
                  onChange={e => handleChange('accusedParty', 'id', e.target.value)}
                  placeholder={t('filing.verification.accusedParty.businessIdPlaceholder', 'Business registration number, tax ID, etc.')}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2" style={{ fontSize: '0.875rem' }}>
                  {t('filing.verification.accusedParty.address', 'Address')} <span className="text-red-500">*</span>
                </label>
                <GoogleAddressInput
                  value={localEdits.accusedParty?.address ?? accused.address ?? ''}
                  onChange={(value) => handleChange('accusedParty', 'address', value)}
                  onAddressSelect={(address: FormattedAddress) => {
                    handleChange('accusedParty', 'address', address.formattedAddress);
                  }}
                  placeholder={t('filing.verification.accusedParty.businessAddressPlaceholder', 'Complete business address')}
                  addressType="business"
                  required
                  className="w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2" style={{ fontSize: '0.875rem' }}>
                  {t('filing.verification.accusedParty.contact', 'Contact')} <span className="text-red-500">*</span>
                </label>
                <input
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 min-h-[44px]"
                  style={{ fontSize: '1rem' }}
                  value={localEdits.accusedParty?.contact ?? accused.contact ?? ''}
                  onChange={e => handleChange('accusedParty', 'contact', e.target.value)}
                  placeholder={t('filing.verification.accusedParty.contactPlaceholder', 'Contact (email, phone, etc.)')}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2" style={{ fontSize: '0.875rem' }}>
                  {t('filing.verification.accusedParty.details', 'Details')}
                </label>
                <textarea
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                  style={{ fontSize: '1rem' }}
                  value={localEdits.accusedParty?.details ?? accused.details ?? ''}
                  onChange={e => handleChange('accusedParty', 'details', e.target.value)}
                  placeholder={t('filing.verification.accusedParty.detailsPlaceholder', 'Additional details')}
                  rows={3}
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium min-h-[44px]"
                  style={{ fontSize: '1rem' }}
                  onClick={() => saveEdit('accusedParty')}
                >
                  {t('filing.verification.save', 'Save')}
                </button>
                <button
                  className="px-6 py-3 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors font-medium min-h-[44px]"
                  style={{ fontSize: '1rem' }}
                  onClick={() => stopEdit('accusedParty')}
                >
                  {t('filing.verification.cancel', 'Cancel')}
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600 mb-1" style={{ fontSize: '0.875rem' }}>
                    {t('filing.verification.accusedParty.name', 'Name')}
                  </p>
                  <p className="text-gray-900 font-medium" style={{ fontSize: '1rem' }}>
                    {accused.name || <span className="text-gray-400 italic">Not provided</span>}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1" style={{ fontSize: '0.875rem' }}>
                    {t('filing.verification.accusedParty.type', 'Type')}
                  </p>
                  <p className="text-gray-900" style={{ fontSize: '1rem' }}>
                    {accused.isBusiness === true ? (
                      <span className="inline-flex items-center px-3 py-1 rounded-full bg-blue-100 text-blue-800 text-sm">
                        Business/Organization
                      </span>
                    ) : accused.isBusiness === false ? (
                      <span className="inline-flex items-center px-3 py-1 rounded-full bg-green-100 text-green-800 text-sm">
                        Individual Person
                      </span>
                    ) : (
                      <span className="text-gray-400 italic">Not specified</span>
                    )}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600 mb-1" style={{ fontSize: '0.875rem' }}>
                    {accused.isBusiness ? t('filing.verification.accusedParty.businessId', 'Business ID') : t('filing.verification.accusedParty.personalId', 'Personal ID')}
                  </p>
                  <p className="text-gray-900" style={{ fontSize: '1rem' }}>
                    {accused.id || <span className="text-gray-400 italic">Not provided</span>}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1" style={{ fontSize: '0.875rem' }}>
                    {t('filing.verification.accusedParty.contactInformation', 'Contact')}
                  </p>
                  <p className="text-gray-900" style={{ fontSize: '1rem' }}>
                    {accused.contact || <span className="text-gray-400 italic">Not provided</span>}
                  </p>
                </div>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1" style={{ fontSize: '0.875rem' }}>
                  {t('filing.verification.accusedParty.address', 'Address')}
                </p>
                <p className="text-gray-900 bg-gray-50 p-3 rounded-lg" style={{ fontSize: '1rem' }}>
                  {accused.address || <span className="text-gray-400 italic">Not provided</span>}
                </p>
              </div>
              {accused.details && (
                <div>
                  <p className="text-sm text-gray-600 mb-1" style={{ fontSize: '0.875rem' }}>
                    {t('filing.verification.accusedParty.businessDetails', 'Additional Details')}
                  </p>
                  <ProgressiveDisclosure>
                    <p className="text-gray-900 bg-gray-50 p-3 rounded-lg" style={{ fontSize: '1rem' }}>
                      {accused.details}
                    </p>
                  </ProgressiveDisclosure>
                </div>
              )}
            </div>
          )}
        </SummaryCard>

        {/* Case Summary Card */}
        <SummaryCard
          title={t('filing.verification.sections.caseSummary', 'Case Summary')}
          icon={<DocumentTextIcon className="w-6 h-6" />}
          editable
          onEdit={() => startEdit('summary')}
          required
        >
          {editState['summary'] ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2" style={{ fontSize: '0.875rem' }}>
                  {t('filing.verification.caseSummary.summary', 'Summary')}
                </label>
                <textarea
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                  style={{ fontSize: '1rem' }}
                  value={localEdits.summary?.text ?? summary}
                  onChange={e => handleChange('summary', 'text', e.target.value)}
                  placeholder={t('filing.verification.caseSummary.summaryPlaceholder', 'Summary')}
                  rows={4}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2" style={{ fontSize: '0.875rem' }}>
                  {t('filing.verification.caseSummary.estimatedTimeline', 'Estimated Timeline')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 min-h-[44px]"
                  style={{ fontSize: '1rem' }}
                  value={(() => {
                    try {
                      const dateValue = localEdits.summary?.estimatedTimeline || data.aiAnalysis?.estimatedTimeline;
                      if (!dateValue) return '';
                      const date = new Date(dateValue);
                      if (isNaN(date.getTime())) return '';
                      return date.toISOString().split('T')[0];
                    } catch {
                      return '';
                    }
                  })()}
                  onChange={e => handleChange('summary', 'estimatedTimeline', e.target.value ? new Date(e.target.value) : null)}
                />
                {errors['aiAnalysis.estimatedTimeline'] && (
                  <p className="mt-2 text-sm text-red-600">{errors['aiAnalysis.estimatedTimeline']}</p>
                )}
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium min-h-[44px]"
                  style={{ fontSize: '1rem' }}
                  onClick={() => saveEdit('summary')}
                >
                  {t('filing.verification.save', 'Save')}
                </button>
                <button
                  className="px-6 py-3 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors font-medium min-h-[44px]"
                  style={{ fontSize: '1rem' }}
                  onClick={() => stopEdit('summary')}
                >
                  {t('filing.verification.cancel', 'Cancel')}
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <ProgressiveDisclosure previewLines={4}>
                <p className="text-gray-900 leading-relaxed" style={{ fontSize: '1rem', lineHeight: '1.625' }}>
                  {summary || <span className="text-gray-400 italic">No summary provided</span>}
                </p>
              </ProgressiveDisclosure>
              <div className="pt-3 border-t border-gray-200">
                <p className="text-sm text-gray-600 mb-1" style={{ fontSize: '0.875rem' }}>
                  {t('filing.verification.caseSummary.estimatedTimeline', 'Estimated Timeline')}
                </p>
                <p className="text-gray-900 font-medium" style={{ fontSize: '1rem' }}>
                  {data.aiAnalysis?.estimatedTimeline
                    ? new Date(data.aiAnalysis.estimatedTimeline).toLocaleDateString()
                    : <span className="text-gray-400 italic">Not specified</span>
                  }
                </p>
              </div>
            </div>
          )}
        </SummaryCard>

        {/* Estimated Claim Amount Card */}
        <SummaryCard
          title={t('filing.verification.sections.estimatedClaimAmount', 'Estimated Claim Amount')}
          icon={<CurrencyDollarIcon className="w-6 h-6" />}
          editable
          onEdit={() => startEdit('estimatedClaimAmount')}
          required
        >
          {editState['estimatedClaimAmount'] ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2" style={{ fontSize: '0.875rem' }}>
                  {t('filing.verification.estimatedClaimAmount.currency', 'Currency')} <span className="text-red-500">*</span>
                </label>
                <select
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 min-h-[44px]"
                  style={{ fontSize: '1rem' }}
                  value={localEdits.estimatedClaimAmount?.currency ?? estimatedClaimAmount.currency}
                  onChange={e => handleChange('estimatedClaimAmount', 'currency', e.target.value)}
                >
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                  <option value="JPY">JPY</option>
                  <option value="CNY">CNY</option>
                  <option value="INR">INR</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2" style={{ fontSize: '0.875rem' }}>
                  Justification <span className="text-red-500">*</span>
                </label>
                <textarea
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                  style={{ fontSize: '1rem' }}
                  value={localEdits.estimatedClaimAmount?.justification ?? estimatedClaimAmount.justification}
                  onChange={e => handleChange('estimatedClaimAmount', 'justification', e.target.value)}
                  placeholder="Reason for the estimated claim amount"
                  rows={3}
                />
              </div>
              <div className="space-y-2">
                {(localEdits.estimatedClaimAmount?.breakdown || []).map((item: any, index: number) => (
                  <div key={index} className="flex gap-3 items-start border rounded-lg p-3 bg-gray-50">
                    <div className="flex-1">
                      <input
                        className="w-full border border-gray-300 rounded px-3 py-2 mb-2 min-h-[44px]"
                        style={{ fontSize: '1rem' }}
                        value={item.label ?? ''}
                        onChange={e => {
                          const newBreakdown = [...(localEdits.estimatedClaimAmount?.breakdown || [])];
                          newBreakdown[index] = { ...newBreakdown[index], label: e.target.value };
                          setLocalEdits(prev => ({ ...prev, estimatedClaimAmount: { ...prev.estimatedClaimAmount, breakdown: newBreakdown } }));
                        }}
                        placeholder="e.g., Medical Expenses"
                      />
                    </div>
                    <div className="w-32">
                      <input
                        type="number"
                        className="w-full border border-gray-300 rounded px-3 py-2 text-right min-h-[44px]"
                        style={{ fontSize: '1rem' }}
                        value={item.amount ?? 0}
                        onChange={e => {
                          const newBreakdown = [...(localEdits.estimatedClaimAmount?.breakdown || [])];
                          newBreakdown[index] = { ...newBreakdown[index], amount: parseFloat(e.target.value) };
                          setLocalEdits(prev => ({ ...prev, estimatedClaimAmount: { ...prev.estimatedClaimAmount, breakdown: newBreakdown } }));
                        }}
                        placeholder="0.00"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        const newBreakdown = (localEdits.estimatedClaimAmount?.breakdown || []).filter((_: any, i: number) => i !== index);
                        setLocalEdits(prev => ({ ...prev, estimatedClaimAmount: { ...prev.estimatedClaimAmount, breakdown: newBreakdown } }));
                      }}
                      className="text-red-600 hover:text-red-800 text-sm font-medium min-h-[44px] px-3"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
              <button
                type="button"
                onClick={() => {
                  setLocalEdits(prev => ({
                    ...prev,
                    estimatedClaimAmount: {
                      ...prev.estimatedClaimAmount,
                      breakdown: [...(prev.estimatedClaimAmount?.breakdown || []), { label: '', amount: 0 }]
                    }
                  }));
                }}
                className="w-full border-2 border-dashed border-gray-300 rounded-lg p-4 text-gray-600 hover:border-gray-400 hover:text-gray-800 transition-colors min-h-[44px]"
                style={{ fontSize: '1rem' }}
              >
                + Add Item
              </button>
              <div className="flex justify-between items-center pt-4 border-t border-gray-200">
                <span className="font-bold text-gray-900" style={{ fontSize: '1rem' }}>Total Amount:</span>
                <span className="font-bold text-blue-700 text-xl">
                  {((localEdits.estimatedClaimAmount?.breakdown || []).reduce((sum: number, item: any) => sum + (parseFloat(item.amount) || 0), 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {localEdits.estimatedClaimAmount?.currency ?? estimatedClaimAmount.currency}
                </span>
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium min-h-[44px]"
                  style={{ fontSize: '1rem' }}
                  onClick={() => saveEdit('estimatedClaimAmount')}
                >
                  {t('filing.verification.save', 'Save')}
                </button>
                <button
                  className="px-6 py-3 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors font-medium min-h-[44px]"
                  style={{ fontSize: '1rem' }}
                  onClick={() => stopEdit('estimatedClaimAmount')}
                >
                  {t('filing.verification.cancel', 'Cancel')}
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                <span className="text-gray-600" style={{ fontSize: '1rem' }}>Total Claim Amount:</span>
                <span className="font-bold text-blue-700 text-2xl">
                  {estimatedClaimAmount.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {estimatedClaimAmount.currency}
                </span>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1" style={{ fontSize: '0.875rem' }}>Justification:</p>
                <ProgressiveDisclosure>
                  <p className="text-gray-900" style={{ fontSize: '1rem' }}>
                    {estimatedClaimAmount.justification || <span className="text-gray-400 italic">No justification provided</span>}
                  </p>
                </ProgressiveDisclosure>
              </div>
              {estimatedClaimAmount.breakdown.length > 0 && (
                <div>
                  <p className="text-sm text-gray-600 mb-2" style={{ fontSize: '0.875rem' }}>Breakdown:</p>
                  <div className="space-y-2">
                    {estimatedClaimAmount.breakdown.map((item: any, index: number) => (
                      <div key={index} className="flex justify-between items-center bg-gray-50 p-3 rounded-lg">
                        <span className="text-gray-900" style={{ fontSize: '1rem' }}>{item.label}</span>
                        <span className="font-medium text-gray-900" style={{ fontSize: '1rem' }}>
                          {item.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {estimatedClaimAmount.currency}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </SummaryCard>

        {/* Your Information Card */}
        <SummaryCard
          title={t('filing.verification.sections.yourInformation', 'Your Information')}
          icon={<UserCircleIcon className="w-6 h-6" />}
          editable
          onEdit={() => startEdit('userInfo')}
          required
        >
          {editState['userInfo'] ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2" style={{ fontSize: '0.875rem' }}>
                    {t('filing.verification.userInfo.firstName', 'First Name')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 min-h-[44px]"
                    style={{ fontSize: '1rem' }}
                    value={localEdits.userInfo?.firstName ?? userInfo.firstName ?? ''}
                    onChange={e => handleChange('userInfo', 'firstName', e.target.value)}
                    placeholder={t('filing.verification.userInfo.firstNamePlaceholder', 'First Name')}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2" style={{ fontSize: '0.875rem' }}>
                    {t('filing.verification.userInfo.lastName', 'Last Name')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 min-h-[44px]"
                    style={{ fontSize: '1rem' }}
                    value={localEdits.userInfo?.lastName ?? userInfo.lastName ?? ''}
                    onChange={e => handleChange('userInfo', 'lastName', e.target.value)}
                    placeholder={t('filing.verification.userInfo.lastNamePlaceholder', 'Last Name')}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2" style={{ fontSize: '0.875rem' }}>
                  {t('filing.verification.userInfo.email', 'Email')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 min-h-[44px]"
                  style={{ fontSize: '1rem' }}
                  value={localEdits.userInfo?.email ?? userInfo.email ?? ''}
                  onChange={e => handleChange('userInfo', 'email', e.target.value)}
                  placeholder={t('filing.verification.userInfo.emailPlaceholder', 'Email')}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2" style={{ fontSize: '0.875rem' }}>
                  {t('filing.verification.userInfo.phone', 'Phone Number')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 min-h-[44px]"
                  style={{ fontSize: '1rem' }}
                  value={localEdits.userInfo?.phone ?? userInfo.phone ?? ''}
                  onChange={e => handleChange('userInfo', 'phone', e.target.value)}
                  placeholder={t('filing.verification.userInfo.phonePlaceholder', 'Phone Number')}
                />
              </div>
              <div>
                <GoogleAddressInput
                  label={t('filing.verification.userInfo.address', 'Address')}
                  value={localEdits.userInfo?.address ?? userInfo.address ?? ''}
                  onChange={(value) => handleChange('userInfo', 'address', value)}
                  onAddressSelect={(address: FormattedAddress) => {
                    handleChange('userInfo', 'address', address.formattedAddress);
                    if (address.countryCode) handleChange('userInfo', 'country', address.countryCode);
                  }}
                  placeholder={t('filing.verification.userInfo.addressPlaceholder', 'Complete Address')}
                  addressType="personal"
                  required
                  helpText={t('filing.verification.userInfo.addressHelp', 'Enter your complete residential address')}
                  className="w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2" style={{ fontSize: '0.875rem' }}>
                  {t('filing.verification.userInfo.country', 'Country')}
                </label>
                <select
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 min-h-[44px]"
                  style={{ fontSize: '1rem' }}
                  value={localEdits.userInfo?.country ?? userInfo.country ?? 'US'}
                  onChange={e => handleChange('userInfo', 'country', e.target.value as SupportedCountry)}
                >
                  <option value="US">United States</option>
                  <option value="UK">United Kingdom</option>
                  <option value="IL">Israel</option>
                  <option value="AE">United Arab Emirates</option>
                  <option value="DE">Germany</option>
                  <option value="FR">France</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2" style={{ fontSize: '0.875rem' }}>
                  {t('filing.verification.userInfo.dateOfBirth', 'Date of Birth')}
                </label>
                <input
                  type="date"
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 min-h-[44px]"
                  style={{ fontSize: '1rem' }}
                  value={localEdits.userInfo?.dateOfBirth ?? userInfo.dateOfBirth ?? ''}
                  onChange={e => handleChange('userInfo', 'dateOfBirth', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2" style={{ fontSize: '0.875rem' }}>
                  {t('filing.verification.userInfo.occupation', 'Occupation')}
                </label>
                <input
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 min-h-[44px]"
                  style={{ fontSize: '1rem' }}
                  value={localEdits.userInfo?.occupation ?? userInfo.occupation ?? ''}
                  onChange={e => handleChange('userInfo', 'occupation', e.target.value)}
                  placeholder={t('filing.verification.userInfo.occupationPlaceholder', 'Occupation')}
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium min-h-[44px]"
                  style={{ fontSize: '1rem' }}
                  onClick={() => saveEdit('userInfo')}
                >
                  {t('filing.verification.save', 'Save')}
                </button>
                <button
                  className="px-6 py-3 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors font-medium min-h-[44px]"
                  style={{ fontSize: '1rem' }}
                  onClick={() => stopEdit('userInfo')}
                >
                  {t('filing.verification.cancel', 'Cancel')}
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600 mb-1" style={{ fontSize: '0.875rem' }}>
                    {t('filing.verification.userInfo.name', 'Name')}
                  </p>
                  <p className="text-gray-900 font-medium" style={{ fontSize: '1rem' }}>
                    {userInfo.firstName && userInfo.lastName
                      ? `${userInfo.firstName} ${userInfo.lastName}`
                      : <span className="text-gray-400 italic">Not provided</span>
                    }
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1" style={{ fontSize: '0.875rem' }}>
                    {t('filing.verification.userInfo.email', 'Email')}
                  </p>
                  <p className="text-gray-900" style={{ fontSize: '1rem' }}>
                    {userInfo.email || <span className="text-gray-400 italic">Not provided</span>}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600 mb-1" style={{ fontSize: '0.875rem' }}>
                    {t('filing.verification.userInfo.phone', 'Phone')}
                  </p>
                  <p className="text-gray-900" style={{ fontSize: '1rem' }}>
                    {userInfo.phone || <span className="text-gray-400 italic">Not provided</span>}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1" style={{ fontSize: '0.875rem' }}>
                    {t('filing.verification.userInfo.dateOfBirth', 'Date of Birth')}
                  </p>
                  <p className="text-gray-900" style={{ fontSize: '1rem' }}>
                    {userInfo.dateOfBirth || <span className="text-gray-400 italic">Not provided</span>}
                  </p>
                </div>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1" style={{ fontSize: '0.875rem' }}>
                  {t('filing.verification.userInfo.address', 'Address')}
                </p>
                <p className="text-gray-900 bg-gray-50 p-3 rounded-lg" style={{ fontSize: '1rem' }}>
                  {userInfo.address || <span className="text-gray-400 italic">Not provided</span>}
                </p>
              </div>
              {userInfo.occupation && (
                <div>
                  <p className="text-sm text-gray-600 mb-1" style={{ fontSize: '0.875rem' }}>
                    {t('filing.verification.userInfo.occupation', 'Occupation')}
                  </p>
                  <p className="text-gray-900" style={{ fontSize: '1rem' }}>{userInfo.occupation}</p>
                </div>
              )}
            </div>
          )}
        </SummaryCard>

        {/* Evidence Card */}
        {data.files && data.files.length > 0 && (
          <SummaryCard
            title={t('filing.verification.sections.evidence', 'Evidence & Documents')}
            icon={<DocumentTextIcon className="w-6 h-6" />}
          >
            <EvidenceCard files={data.files} />
          </SummaryCard>
        )}

        {/* Legal Disclaimer */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
          <div className="flex items-start gap-3">
            <ExclamationCircleIcon className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-1" />
            <div>
              <h4 className="font-semibold text-yellow-900 mb-2" style={{ fontSize: '1rem' }}>
                {t('filing.verification.legalDisclaimer.title', 'Legal Disclaimer')}
              </h4>
              <ProgressiveDisclosure previewLines={2}>
                <p className="text-yellow-800 text-sm leading-relaxed" style={{ fontSize: '0.875rem', lineHeight: '1.5' }}>
                  {t('filing.verification.legalDisclaimer.text', 'By proceeding, you confirm that the information provided is accurate and complete to the best of your knowledge. This platform provides automated document generation and legal information but does not constitute legal advice. Consult with a licensed attorney for advice specific to your situation.')}
                </p>
              </ProgressiveDisclosure>
            </div>
          </div>
        </div>
      </div>

      {/* Sticky Thumb Zone Navigation - Bottom 40% */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-50">
        <div className="max-w-5xl mx-auto px-4 py-4">
          {Object.values(errors).filter(err => err).length > 0 && (
            <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center gap-2">
                <ExclamationCircleIcon className="w-5 h-5 text-red-600 flex-shrink-0" />
                <div className="text-sm text-red-800">
                  {Object.values(errors).filter(err => err).map((err, idx) => <div key={idx}>{err}</div>)}
                </div>
              </div>
            </div>
          )}
          <div className={`flex gap-3 ${isEditMode ? 'justify-end' : 'justify-between'}`}>
            {!isEditMode && (
              <button
                className="px-8 py-4 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors font-semibold min-h-[56px] text-lg"
                onClick={onBack}
                disabled={isSubmitting}
                style={{ fontSize: '1.125rem' }}
              >
                {t('common.back', 'Back')}
              </button>
            )}
            <button
              className="px-8 py-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold min-h-[56px] text-lg disabled:bg-gray-400 disabled:cursor-not-allowed"
              onClick={handleNext}
              disabled={isSubmitting}
              style={{ fontSize: '1.125rem' }}
            >
              {isSubmitting
                ? t('filing.verification.saving', 'Saving...')
                : (isEditMode ? t('filing.verification.regenerateDocuments', 'Regenerate Documents') : t('common.next', 'Next'))
              }
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerificationStep;
